function question_has_id(question) {
  return Number(question.id || 0) > 0;
}

function normalize_manual_mapping_label(value = '') {
  return String(value || '').trim().toLowerCase().replace(/[_-]+/g, ' ');
}

function get_manual_mapping_question_id(mapping = {}) {
  return Number(
    mapping?.manual_section_question
    || mapping?.verification_section_question
    || 0
  ) || null;
}

function find_mapping_for_manual_question(question = {}, mappings = []) {
  const question_id = Number(question?.id || 0) || null;
  const question_label = normalize_manual_mapping_label(question?.label || '');
  const available_mappings = Array.isArray(mappings) ? mappings : [];

  if (question_id) {
    const id_match = available_mappings.find((mapping) => get_manual_mapping_question_id(mapping) === question_id);
    if (id_match) return id_match;
  }

  if (!question_label) return null;

  return available_mappings.find((mapping) => (
    normalize_manual_mapping_label(
      mapping?.manual_question_text
      || mapping?.verification_question_text
      || mapping?.onboarding_question_text
      || ''
    ) === question_label
  )) || null;
}

function hydrate_manual_review_question(question = {}, mappings = [], resolve_answer) {
  const mapping = find_mapping_for_manual_question(question, mappings);
  const fallback_mapping = {
    onboarding_question_text: question?.label || '',
    manual_question_text: question?.label || '',
    verification_question_text: question?.label || ''
  };
  const submitted_answer = typeof resolve_answer === 'function'
    ? resolve_answer(mapping || fallback_mapping)
    : null;
  const submitted_answer_data = submitted_answer && typeof submitted_answer === 'object'
    ? submitted_answer
    : {};

  return {
    ...question,
    answer: submitted_answer_data.answer || question?.answer || '',
    file_url: submitted_answer_data.file_url || question?.file_url || '',
    file_name: submitted_answer_data.file_name || question?.file_name || '',
    type: submitted_answer_data.type || question?.type || '',
    mapping: mapping || question?.mapping || null,
    source_label: mapping?.onboarding_question_text || question?.source_label || ''
  };
}

function hydrate_manual_review_sub_section(sub_section = {}, mappings = [], resolve_answer) {
  return {
    ...sub_section,
    questions: (Array.isArray(sub_section?.questions) ? sub_section.questions : [])
      .map((question) => hydrate_manual_review_question(question, mappings, resolve_answer))
      .filter(question_has_id)
  };
}

function hydrate_manual_review_section(section = {}, mappings = [], resolve_answer) {
  return {
    ...section,
    sub_sections: (Array.isArray(section?.sub_sections) ? section.sub_sections : [])
      .map((sub_section) => hydrate_manual_review_sub_section(sub_section, mappings, resolve_answer))
      .filter((sub_section) => sub_section.questions.length)
  };
}

function count_manual_review_section_questions(form_section = {}) {
  return (Array.isArray(form_section?.sections) ? form_section.sections : [])
    .reduce((section_total, section) => (
      section_total + (Array.isArray(section?.sub_sections) ? section.sub_sections : [])
        .reduce((sub_total, sub_section) => (
          sub_total + (Array.isArray(sub_section?.questions) ? sub_section.questions.length : 0)
        ), 0)
    ), 0);
}

export function build_manual_review_sections_from_form_sections(
  item = {},
  manual_form_sections = [],
  manual_mappings = [],
  resolve_answer,
  get_manual_review_rule_name
) {
  const hydrated_sections = (Array.isArray(manual_form_sections) ? manual_form_sections : [])
    .map((section) => hydrate_manual_review_section(section, manual_mappings, resolve_answer))
    .filter((section) => section.sub_sections.length);
  if (!hydrated_sections.length) return [];

  return hydrated_sections.map((form_section) => ({
    id: form_section.id,
    form_name: get_manual_review_rule_name(item),
    answered_count: count_manual_review_section_questions({ sections: [form_section] }),
    sections: [form_section]
  }));
}

export function build_field_run_by_question(field_runs = []) {
  return new Map(
    field_runs.map((field_run) => [Number(field_run?.section_question || 0), field_run])
  );
}

function decorate_review_question(question = {}, field_run_by_question = new Map()) {
  const existing_run = field_run_by_question.get(Number(question?.id || 0));
  return {
    ...question,
    section_question_id: Number(question?.id || 0) || null,
    status: ['match', 'matched', 'yes', 'pass', 'passed', 'verified', 'success']
      .includes(String(existing_run?.match_status || '').trim().toLowerCase().replace(/[\s-]+/g, '_'))
      ? 'pass'
      : ['mismatch', 'no', 'fail', 'failed', 'rejected', 'reject', 'error']
        .includes(String(existing_run?.match_status || '').trim().toLowerCase().replace(/[\s-]+/g, '_'))
        ? 'fail'
        : 'na',
    comment: existing_run?.reviewer_comment || '',
    detail: question?.source_label
      ? `Review against submitted value for ${question.source_label}.`
      : 'Review this manual verification form question.'
  };
}

function decorate_review_sub_section(sub_section = {}, field_run_by_question) {
  return {
    ...sub_section,
    questions: (Array.isArray(sub_section?.questions) ? sub_section.questions : [])
      .map((question) => decorate_review_question(question, field_run_by_question))
  };
}

function decorate_review_section(section = {}, field_run_by_question) {
  return {
    ...section,
    sub_sections: (Array.isArray(section?.sub_sections) ? section.sub_sections : [])
      .map((sub_section) => decorate_review_sub_section(sub_section, field_run_by_question))
      .filter((sub_section) => sub_section.questions.length)
  };
}

export function decorate_review_form_section(form_section = {}, field_run_by_question) {
  return {
    ...form_section,
    sections: (Array.isArray(form_section?.sections) ? form_section.sections : [])
      .map((section) => decorate_review_section(section, field_run_by_question))
      .filter((section) => section.sub_sections.length)
  };
}
